import React, { useState, useEffect } from 'react';
import {
  Input,
  Checkbox,
  Form,
  Row,
  Col,
} from 'antd';
import DatePicker from '../../../DatePicker';
import PropTypes from 'prop-types';
import { FileDashed, FilePdf } from '@phosphor-icons/react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import PrivacyLevel from '../../PrivacyLevel';
import { formatDate, validDOB } from '../../../../global/validation';
import Box from '../../../common/basic/Box';

const UploadSingleRecord = ({
  file, display, errorMessage, uploadRecord, data,
  uploadClicked, isLoading, isOnlyRecord,
  onUploadSuccess, onUploadError, isUploaded, hasCommonPatient,
  patientFirstName: passedPatientFirstName, patientMiddleName: passedPatientMiddleName,
  patientLastName: passedPatientLastName, patientDOB: passedPatientDOB,
  onRequiredInfoCorrect, canPublishForms,
}) => {
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientMiddleName, setPatientMiddleName] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  const [patientDOB, setPatientDOB] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [isFormDocument, setIsFormDocument] = useState(false);
  const [isGenericDocument, setIsGenericDocument] = useState(false);
  const [privacyLevel, setPrivacyLevel] = useState(localStorage.getItem('privacy_level') || 'department');
  const [useSelectedPrivacyLevel, setUseSelectedPrivacyLevel] = useState(true);

  const [hasShownFeedback, setHasShownFeedback] = useState(false);

  const validateName = (firstName, lastName) => {
    if ((firstName && firstName.trim() !== '') && (lastName && lastName.trim() !== '')) return true;
    return false;
  };

  const upload = async () => {
    // Capture the generic toggle with the rest of the per-file inputs at
    // action time. Generic uploads send blank identifiers even when the
    // hidden name/DOB fields still hold previously typed values.
    const isGenericUpload = !hasCommonPatient && isGenericDocument;

    let usedFirstName = '';
    let usedMiddleName = '';
    let usedLastName = '';
    let usedDOB = '';

    if (hasCommonPatient) {
      // Use passed name fields from common patient
      usedFirstName = passedPatientFirstName;
      usedMiddleName = passedPatientMiddleName;
      usedLastName = passedPatientLastName;
      usedDOB = passedPatientDOB;
    } else if (!isGenericUpload) {
      // Use individual patient name fields
      usedFirstName = patientFirstName;
      usedMiddleName = patientMiddleName;
      usedLastName = patientLastName;
      usedDOB = patientDOB;
    }

    const validName = isGenericUpload || validateName(usedFirstName, usedLastName);
    const validDate = validDOB(usedDOB) || usedDOB === '';

    // Do not send request if any fields are invalid
    if (!validName || !validDate || !file) return;

    const usedName = [usedFirstName, usedMiddleName, usedLastName].filter(Boolean).join(' ');

    const dob = formatDate(usedDOB);
    // Set up form data for data. Patient identifiers are always appended,
    // even when blank for generic documents — the backend rejects requests
    // that omit name/dob but accepts them as empty strings.
    const bodyFormData = new FormData();
    bodyFormData.append('name', usedName);
    bodyFormData.append('first_name', usedFirstName || '');
    bodyFormData.append('middle_name', usedMiddleName || '');
    bodyFormData.append('last_name', usedLastName || '');
    bodyFormData.append('dob', dob);
    bodyFormData.append('file', file);
    bodyFormData.append('privacy_level', privacyLevel);
    bodyFormData.append('is_form_document', isFormDocument);
    if (isGenericUpload) bodyFormData.append('document_title', documentTitle);
    const e = await uploadRecord(bodyFormData);
    if (e?.error) {
      const serverError = e.response?.status === 422 ? e.response?.data?.error : null;
      toastr.error(`Error when uploading ${file.name}, ${serverError || e.message || 'please try again'}`);
      onUploadError();
    } else {
      localStorage.setItem('from_page', 'uploaded');
      onUploadSuccess();
    }
    setHasShownFeedback(true);
  };

  useEffect(() => {
    if (isLoading || !uploadClicked || hasShownFeedback) return;

    if (data.url && isOnlyRecord) {
      if (useSelectedPrivacyLevel) {
        localStorage.setItem('privacy_level', privacyLevel);
      } else {
        localStorage.removeItem('privacy_level');
      }
      window.open(`/app/view/${data.url}?from=uploaded`, '_self');
    }
  }, [data]);

  useEffect(() => {
    if (uploadClicked && !isUploaded) {
      upload();
    }
  }, [uploadClicked]);

  // Signal to parent that required info is present when using common patient
  useEffect(() => {
    if (hasCommonPatient) {
      const hasValidName = !!(passedPatientFirstName?.trim() && passedPatientLastName?.trim());
      onRequiredInfoCorrect(hasValidName);
    }
  }, [hasCommonPatient, passedPatientFirstName, passedPatientLastName]);

  useEffect(() => {
    if (!hasCommonPatient) {
      // Generic documents bypass the patient-name requirement; otherwise the
      // signal recomputes from the (possibly preserved) typed values.
      const isValid = isGenericDocument || !!(patientFirstName && patientLastName);
      onRequiredInfoCorrect(isValid);
    }
  }, [patientFirstName, patientLastName, hasCommonPatient, isGenericDocument]);

  const handleFirstNameChange = (event) => {
    setPatientFirstName(event.target.value);
  };

  const handleMiddleNameChange = (event) => {
    setPatientMiddleName(event.target.value);
  };

  const handleLastNameChange = (event) => {
    setPatientLastName(event.target.value);
  };

  const handleDateChange = (_, dateString) => setPatientDOB(dateString);
  const handleDocumentTitleChange = (event) => setDocumentTitle(event.target.value);
  const handleIsFormDocumentChange = (event) => setIsFormDocument(event.target.checked);
  const handleIsGenericDocumentChange = (event) => setIsGenericDocument(event.target.checked);

  const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];

  return (
    <Box className="upload-record-modal__file-card">
      {file?.name && (
        <div className="upload-record-modal__file-chip">
          <FilePdf size={14} weight="fill" />
          <span>{file?.name}</span>
        </div>
      )}
      <Form>
        {
          !hasCommonPatient && (
          <>
            {
              isGenericDocument
                ? (
                  <>
                    <Form.Item>
                      <div className="upload-record-modal__generic-stub">
                        <FileDashed size={16} />
                        <span>Generic document — no patient info</span>
                      </div>
                    </Form.Item>
                    <Form.Item
                      className="upload-record-modal__generic-title"
                      label="Document Title (optional)"
                    >
                      <Input
                        value={documentTitle}
                        placeholder="Title"
                        maxLength={255}
                        onChange={handleDocumentTitleChange}
                      />
                    </Form.Item>
                  </>
                )
                : (
                  <>
                    <Form.Item label="Patient Name">
                      <Row gutter={8}>
                        <Col span={8}>
                          <Form.Item
                            name="firstName"
                          >
                            <Input
                              placeholder="First"
                              onChange={handleFirstNameChange}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            name="middleName"
                          >
                            <Input
                              placeholder="Middle"
                              onChange={handleMiddleNameChange}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            name="lastName"
                          >
                            <Input
                              placeholder="Last"
                              onChange={handleLastNameChange}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form.Item>
                    <Form.Item
                      label="Date of Birth MM/DD/YYYY (optional)"
                    >
                      <DatePicker
                        onChange={handleDateChange}
                        format={dateFormat}
                        value={moment(patientDOB).isValid() && moment(patientDOB)}
                      />
                    </Form.Item>
                  </>
                )
            }
            <Form.Item>
              <Checkbox
                checked={isGenericDocument}
                onChange={handleIsGenericDocumentChange}
              >
                This document is not tied to a patient
              </Checkbox>
            </Form.Item>
          </>
          )
        }
        {
          canPublishForms
          && (
          <Form.Item>
            <Checkbox
              onChange={handleIsFormDocumentChange}
            >
              Is this document a Form Template?
            </Checkbox>
          </Form.Item>
          )
        }
        <p className="upload-record-modal__section-title">Viewing Permissions</p>
        <PrivacyLevel
          valueSelected={privacyLevel}
          display={display}
          handleChange={(value) => {
          // eslint-disable-next-line no-shadow
            const privacyLevel = value;
            setPrivacyLevel(privacyLevel);
          }}
        />
        <br />
        {
          display && isOnlyRecord
          && (
            <React.Fragment>
              <Checkbox onChange={(event) => setUseSelectedPrivacyLevel(event.target.checked)} checked={useSelectedPrivacyLevel}>
                Use selected privacy level for all future uploads.
              </Checkbox>
            </React.Fragment>
          )
        }
      </Form>
    </Box>
  );
};

UploadSingleRecord.contextTypes = {
  router: PropTypes.object.isRequired,
};

UploadSingleRecord.defaultProps = {
  errorMessage: '',
  data: [],
};

UploadSingleRecord.propTypes = {
  uploadRecord: PropTypes.func.isRequired,
  display: PropTypes.bool.isRequired,
  file: PropTypes.shape({
    originFileObj: PropTypes.shape({}),
  }).isRequired,
  errorMessage: PropTypes.string,
  data: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({}),
  ]),
};

export default connect((state) => ({
  data: state.records.data,
  isLoading: state.records.isLoading,
}))(withRouter(UploadSingleRecord));
